import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectUser, selectGreenhouse } from 'state';
import { Header } from 'components/Header';
import { GreenhouseList } from '../../components/GreenhouseList';
import { FileUploader } from '../../components/FileUploader/FileUploader';
import {
  FilesContainer,
  HelpText,
  MissionActions,
  MissionDateInfo,
  MissionItem,
  SortButton,
  MissionItemInfo,
  MissionListContainer,
  MissionListTitleContainer,
  MissionsContainer,
  Title,
  UploadContainer,
  UploadFileItem,
  UploadFileItemInfo,
  DownloadButton,
  PaginationBlock,
  MissionItemBlock,
  FailReasonText
} from './Upload.styles';
import { API } from '../../api';
import { Icon } from '../../components/_shared/Icon';
import { StyledButton } from '../../components/_shared/Button';
import {
  ProgressBarContainer,
  ProgressBarFill,
  ProgressBarItem,
  ProgressBarText,
  UploadInfo
} from '../../components/FileUploader/FileUploader.styles';
import {
  StyledPaper,
  Tab,
  TabContainer,
  Tabs,
  TabsLine
} from '../Dashboard/Dashboard.styles';
import { DATA_NOT_AVAILABLE } from '../../_constants';
import { Pagination } from '../../components/_shared/Pagination';

const tabNames = ['Mission List', 'Upload New Mission'];

export const Upload = () => {
  const user = useSelector(selectUser);
  const { locations, selectedGreenhouse } = useSelector(selectGreenhouse);
  const customerId = user?.customer?.[0]?.external_id;
  const [missions, setMissions] = useState([]);
  const [sortAsc, setSortAsc] = useState(true);
  const [customer, setCustomer] = useState();
  const [location, setLocation] = useState();
  const [selectedTab, setSelectedTab] = useState(tabNames[0]);
  const [dataCount, setDataCount] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [plants, setPlants] = useState([]);
  const [cropPipelines, setCropPipelines] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const showDetails = (index, show) => {
    setMissions(prev =>
      prev.map((item, i) => (i === index ? { ...item, showDetails: !show } : item))
    );
  };
  const capitalizeFirstLetter = str => {
    if (typeof str !== 'string' || str.length === 0) {
      return str;
    }
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };
  function formatDate(dateStr) {
    const d = new Date(dateStr);
    return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}.${d.getFullYear()} ${d.getHours()}:${d
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;
  }
  function formatText(textStr) {
    return textStr
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  function renderTab(tab) {
    switch (tab) {
      case tabNames[1]:
        return (
          <FileUploader
            customer={customer}
            location={location}
            plants={plants}
            cropPipelines={cropPipelines}
            devices={devices}
            onUploadComplete={() => getMissions(customer, location, currentPage)}
          />
        );
      case tabNames[0]:
        return (
          <MissionListContainer>
            <MissionListTitleContainer>
              <Title>Missions List</Title>
              <SortButton
                title='Change order of missions'
                onClick={() => {
                  setSortAsc(!sortAsc);
                  getMissions(customer, location, currentPage);
                }}
                icon={
                  <Icon width={16} height={16}>
                    {sortAsc ? 'ExpandUp' : 'ExpandDown'}
                  </Icon>
                }
                isActive={true}
              >
                {sortAsc ? 'Latest First' : 'Oldest First'}
              </SortButton>
            </MissionListTitleContainer>
            <MissionsContainer>
              {missions.length === 0 && (
                <UploadInfo>
                  <HelpText>There are no missions to display.</HelpText>
                </UploadInfo>
              )}
              {missions.map(mission => (
                <MissionItemBlock key={mission.id}>
                  <MissionItem>
                    <div>
                      <HelpText fontSize={17}>{formatDate(mission.created_at)}</HelpText>
                    </div>
                    <MissionItemInfo>
                      <ProgressBarItem>
                        <ProgressBarContainer title='Progress of processing'>
                          <ProgressBarFill
                            $status={mission.status}
                            $width={mission.progress ?? 100}
                          ></ProgressBarFill>
                          <ProgressBarText>
                            {mission.status === 'RUNNING' &&
                              (mission.progress ? mission.progress + '%' : '')}
                          </ProgressBarText>
                        </ProgressBarContainer>
                      </ProgressBarItem>
                      <Icon title={'Mission status ' + mission.status}>
                        {mission.status === 'DONE' ||
                        mission.status === 'FAILED' ||
                        mission.status === 'PLANNED'
                          ? capitalizeFirstLetter(mission.status)
                          : 'Process'}
                      </Icon>
                      <StyledButton
                        title='Show/Hide Details'
                        onClick={() => {
                          mission.showDetails
                            ? ''
                            : getMissionFiles(customer, location, mission.mission);
                          showDetails(mission.id, mission.showDetails);
                        }}
                      >
                        <Icon width={20} height={20}>
                          {mission.showDetails ? 'ExpandUp' : 'ExpandDown'}
                        </Icon>
                      </StyledButton>
                    </MissionItemInfo>
                  </MissionItem>
                  <FilesContainer>
                    {mission.showDetails && (
                      <MissionActions>
                        <StyledButton
                          title='Click to upload files'
                          onClick={() => openUploader(mission.id, mission.openUploader)}
                        >
                          <Icon width={20} height={20}>
                            {!mission.openUploader ? 'Plus' : 'Minus'}
                          </Icon>
                        </StyledButton>
                        <StyledButton
                          title='Click to start mission processing'
                          onClick={() =>
                            runMission(customerId, selectedGreenhouse, mission.mission)
                          }
                        >
                          <Icon>{'Play'}</Icon>
                        </StyledButton>
                        <StyledButton
                          title='Click to delete mission'
                          onClick={() =>
                            deleteMission(customerId, selectedGreenhouse, mission.mission)
                          }
                        >
                          <Icon>{'Delete'}</Icon>
                        </StyledButton>
                      </MissionActions>
                    )}
                    {mission.showDetails && (
                      <MissionDateInfo key={mission.id}>
                        {mission.status === 'FAILED' && (
                          <FailReasonText
                            title={mission.reason ?? 'Loading...'}
                            fontSize={14}
                          >
                            Fail Reason: {mission.reason ?? 'Loading...'}
                          </FailReasonText>
                        )}
                        <HelpText fontSize={14}>
                          Mission ID: {mission.mission.replace('grower_', '')}
                        </HelpText>
                        <HelpText fontSize={14}>
                          Plant:{' '}
                          {formatText(mission?.plant ?? 'No longer exists') ||
                            'Not selected'}
                        </HelpText>
                        <HelpText fontSize={14}>
                          Crop Pipeline:{' '}
                          {mission.crop_pipeline
                            ? formatText(mission.crop_pipeline)
                            : 'Not selected'}
                        </HelpText>
                        <HelpText fontSize={14}>
                          Device:{' '}
                          {mission.device ? formatText(mission.device) : 'Not selected'}
                        </HelpText>
                        <HelpText fontSize={14}>
                          Upload Date: {formatDate(mission.created_at)}
                        </HelpText>
                        {formatDate(mission.created_at) !==
                          formatDate(mission.updated_at) && (
                          <HelpText fontSize={12}>
                            Updated Date: {formatDate(mission.updated_at)}
                          </HelpText>
                        )}
                      </MissionDateInfo>
                    )}
                    {mission.showDetails &&
                      mission.files &&
                      mission.files.map(file => (
                        <UploadFileItem key={file.name}>
                          <HelpText>{file.name}</HelpText>
                          <UploadFileItemInfo>
                            <HelpText>
                              {(file.size / (1024 * 1024)).toFixed(2)} MB
                            </HelpText>
                            <StyledButton
                              onClick={() =>
                                deleteFile(
                                  customerId,
                                  selectedGreenhouse,
                                  mission.mission,
                                  file.name
                                )
                              }
                            >
                              <Icon>{'Delete'}</Icon>
                            </StyledButton>
                            <DownloadButton
                              href={file.url}
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              <Icon width={16}>{'Download'}</Icon>
                            </DownloadButton>
                          </UploadFileItemInfo>
                        </UploadFileItem>
                      ))}
                    {mission.openUploader && mission.showDetails && (
                      <FileUploader
                        customer={customer}
                        location={location}
                        mission={mission.mission}
                        plants={plants}
                        cropPipelines={cropPipelines}
                        plant={mission.plant}
                        cropPipeline={mission.crop_pipeline}
                        devices={devices}
                        device={mission.device}
                        onUploadComplete={() =>
                          getMissions(customer, location, currentPage)
                        }
                        uploadedFiles={mission.files?.map(file => file.name)}
                      />
                    )}
                  </FilesContainer>
                </MissionItemBlock>
              ))}
              {dataCount !== 0 && (
                <PaginationBlock>
                  <Pagination
                    currentPage={currentPage}
                    count={dataCount}
                    onSelectCallback={page => getMissions(customer, location, page)}
                  />
                </PaginationBlock>
              )}
            </MissionsContainer>
          </MissionListContainer>
        );
      default:
        return <StyledPaper>{DATA_NOT_AVAILABLE}</StyledPaper>;
    }
  }

  const openUploader = (index, open) => {
    setMissions(prev =>
      prev.map((item, i) => (i === index ? { ...item, openUploader: !open } : item))
    );
  };

  const deleteFile = (customer, location, mission, fileName) => {
    API.upload
      .deleteFile(customer, location, mission, fileName)
      .then(() => getMissions(customer, location, currentPage));
  };

  const deleteMission = (customer, location, mission) => {
    API.upload
      .deleteMission(customer, location, mission)
      .then(() => getMissions(customer, location, currentPage));
  };
  const runMission = (customer, location, mission) => {
    API.upload
      .runMission(customer, location, mission)
      .then(() => getMissions(customer, location, currentPage));
  };
  const getPlants = () => {
    setPlants([]);
    API.upload.getPlants().then(({ data }) => setPlants(data.map(item => item.name)));
  };
  const getCropPipelines = () => {
    setCropPipelines([]);
    API.upload
      .getSubscriptionCrops()
      .then(({ data }) => setCropPipelines(data.map(item => item.service_type)));
  };
  const getDevices = () => {
    const greenhouse = locations.find(({ id }) => id === selectedGreenhouse);
    setDevices(greenhouse?.devices || []);
  };

  const getMissions = (customer, location, page) => {
    setMissions([]);
    setLoading(true);
    API.upload.getMissions(customer, location, page, sortAsc).then(({ data }) => {
      setMissions(
        data.results.missions.map((mission, index) => ({
          ...mission,
          showDetails: false,
          openUploader: false,
          id: index
        }))
      );
      setDataCount(data.count);
      setCurrentPage(page);
      setLoading(false);
    });
  };
  const getMissionFiles = (customer, location, mission) => {
    API.upload.getMissionFiles(customer, location, mission).then(({ data }) => {
      setMissions(prev =>
        prev.map(item =>
          item.mission === mission
            ? {
                ...item,
                files: data.files,
                progress: data.progress,
                status: data.status,
                reason: data.reason
              }
            : item
        )
      );
    });
  };
  useEffect(() => {
    if (customerId !== undefined && selectedGreenhouse !== undefined) {
      setCustomer(customerId);
      setLocation(selectedGreenhouse);
      getDevices();
    }
  }, [customerId, selectedGreenhouse]);
  useEffect(() => {
    getPlants();
    getCropPipelines();
  }, []);
  useEffect(() => {
    if (customer && location) {
      getMissions(customer, location, currentPage);
    }
  }, [customer, location, sortAsc]);
  return (
    <>
      <Header />
      <GreenhouseList />
      <TabContainer padding={1}>
        <Tabs>
          {tabNames.map((v, index) => (
            <Tab
              style={
                !location && index === 1
                  ? { pointerEvents: 'none', opacity: 0.7 }
                  : undefined
              }
              key={v}
              onClick={() => setSelectedTab(v)}
              selected={selectedTab === v}
            >
              {v}
            </Tab>
          ))}
          <TabsLine />
        </Tabs>
        <UploadContainer>{renderTab(selectedTab)}</UploadContainer>
      </TabContainer>
    </>
  );
};
